import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {Box, Container, Grid} from "@mui/material";
import InputControls from "./FormControl/InputControls";
import TemplateDisplay from "./TemplateDisplay/TemplateDisplay";
import Template, {ImageInput, ImageValue, TextInput} from "../../models/Template";
import {SelectedImageData, useMergedScene, useTemplate} from "../../context/TemplateContext";
import {useApiService} from "../../context/ApiServiceContext";
import {AxiosError, AxiosResponse} from "axios";
import {useMobile} from "../../context/MobileContext";
import {useContentEditable} from "./TemplateDisplay/hooks/useContentEditable";
import ContentEditableInfoBar from "./ContentEditableInfoBar";
import {toast} from "react-toastify";
import {useTranslation} from "react-i18next";
import AvailableDesignGrid from "./AvailableDesignsGrid";
import loadFont from "../../utils/fontsLoader";
import ImageDragAndResize from "./FormControl/ImageDragAndResize";
import Asset, {isVideoAsset} from "../../models/Asset";
import {handleImageChangeByWidthAndHeight} from "../../utils/image";
import {styled, Theme} from "@mui/material/styles";
import Button from "@mui/material/Button";
import TopBar from "../drawer/TopBar";
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import ShareModal from "./ShareModal";
import GenerateImageButton from "./FormControl/GenerateImageButton";
import {ShimmerContainer, ShimmerEffect} from "../../utils/shimmer";
import ImageSelectionWrapper from "./FormControl/ImageSelection/ImageSelectionWrapper";
import FloatingImageControlBarWrapper from "./FormControl/ImageSelection/FloatingImageControlBarWrapper";
import ExcelImportExport from "./ExcelImportExport";
import FillModeEditor from "./FillMode/FillModeEditor";
import logger from "../../utils/logger";
import {ElementTransform, SceneTransforms} from "../../scene/types";
import config from "../../config/config";
import {getAccessToken} from "../../services/tokenStore";
import {isDraftBehindTemplate} from "./draftRevision";
import {isUserLayerId} from "../../scene/sceneToKonva";
import {
    collectUserLayerIds,
    buildLayerIndex,
    designKeyOf,
    initialLayerBox,
    MAX_USER_LAYERS_PER_DESIGN,
    newUserLayerId,
    parseUserLayers,
    sanitizeLayerTransforms,
    UserLayer,
    UserLayersByDesign,
    UserLayerStyle,
    validSlots,
} from "../../scene/userLayers";
import {isVideoAsset as isVideoAssetValue} from "../../models/Asset";
import UserLayersPanel from "./FormControl/UserLayersPanel";
import {DEFAULT_VISIBILITY, restoreVisibility} from "../../scene/rowVisibility";
import {resetRow} from "../../scene/rowReposition";
import {designScenes} from "./rows/useRows";
import LayerUndoSnackbar from "./LayerUndoSnackbar";
import RowRepositionBar from "./rows/RowRepositionBar";


export const Controls = styled(Grid)(({theme}) => ({
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '15px',
}));

export const EditContainer = styled(Grid)(({theme}) => ({
    border: "1px solid #FFFFFFA9",
    borderRadius: "22px",
    background: "linear-gradient(220deg, #051726 36%, #025989 100%)",
}));

// Helper function to apply video thumbnail styling with blurred background using Canvas
// Returns the container dimensions used, so caller can update imageTransforms state
async function applyVideoThumbnailStyle(element: HTMLImageElement, thumbnailUrl: string): Promise<{width: number, height: number} | null> {
    try {
        // Get container dimensions
        const container = element.parentElement;
        if (!container) {
            return null;
        }

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        if (containerWidth === 0 || containerHeight === 0) {
            console.warn('Container has zero dimensions, skipping video thumbnail processing');
            return null;
        }

        // Load thumbnail image
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = 'anonymous'; // Enable CORS if needed
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error('Failed to load thumbnail'));
            image.src = thumbnailUrl;
        });

        // Create canvas with container dimensions
        const canvas = document.createElement('canvas');
        canvas.width = containerWidth;
        canvas.height = containerHeight;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
            console.error('Failed to get canvas context for video thumbnail');
            return null;
        }

        // Step 1: Draw blurred background (cover scaling)
        ctx.filter = 'blur(20px)';

        // Calculate cover scaling (fill entire canvas)
        const bgScale = Math.max(
            containerWidth / img.width,
            containerHeight / img.height
        );
        const bgWidth = img.width * bgScale;
        const bgHeight = img.height * bgScale;
        const bgX = (containerWidth - bgWidth) / 2;
        const bgY = (containerHeight - bgHeight) / 2;

        ctx.drawImage(img, bgX, bgY, bgWidth, bgHeight);

        // Step 2: Draw clear thumbnail (contain scaling)
        ctx.filter = 'none';

        // Calculate contain scaling (fit within canvas)
        const fgScale = Math.min(
            containerWidth / img.width,
            containerHeight / img.height
        );
        const fgWidth = img.width * fgScale;
        const fgHeight = img.height * fgScale;
        const fgX = (containerWidth - fgWidth) / 2;
        const fgY = (containerHeight - fgHeight) / 2;

        ctx.drawImage(img, fgX, fgY, fgWidth, fgHeight);

        // Convert canvas to data URL and set as image source
        const compositeDataUrl = canvas.toDataURL('image/png');
        element.src = compositeDataUrl;

        // Reset image element dimensions to match container (the composite already contains the properly positioned video)
        // This is needed because fitImageToContainer sets "cover" dimensions which are larger than the container
        element.style.width = `${containerWidth}px`;
        element.style.height = `${containerHeight}px`;
        element.style.left = '0px';
        element.style.top = '0px';

        return { width: containerWidth, height: containerHeight };

    } catch (error) {
        console.error('Error generating video thumbnail composite:', error);
        // Keep original thumbnail on error
        return null;
    }
}

/** Depth-first find of the editable scene element bound to an input key —
 *  collage cells and grouped elements nest, so a flat scan misses them. */
const findSceneElementByInputKey = (elements: any[] | undefined, inputKey: string | undefined): any => {
    for (const el of elements ?? []) {
        if (el?.inputKey === inputKey && el?.editable) return el;
        const nested = findSceneElementByInputKey(el?.elements, inputKey);
        if (nested) return nested;
    }
    return undefined;
};

/** Depth-first: is the element with this id on the board? */
const hasSceneElementId = (elements: any[] | undefined, id: string): boolean =>
    (elements ?? []).some((el) => el?.id === id || hasSceneElementId(el?.elements, id));

/** All scene element ids across every design (nested `elements` included), used
 *  to drop stale sceneTransforms from a restored draft whose elements no longer
 *  exist in the (possibly re-imported) template. */
const collectSceneElementIds = (template: Template): Set<string> => {
    const ids = new Set<string>();
    const walk = (elements: any[] | undefined) => {
        for (const el of elements ?? []) {
            if (el?.id) ids.add(el.id);
            walk(el?.elements);
        }
    };
    for (const design of template.designs ?? []) walk(design.scene?.elements);
    return ids;
};

const TemplateEditor: React.FC = ({}) => {
    const {templateId} = useParams<{ templateId: string }>();
    const [selectedDesign, setSelectedDesign] = useState<number>(0);
    const [shouldUpdateDesignValues, setShouldUpdateDesignValues] = useState<boolean>(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const apiService = useApiService();
    const {
        template,
        setTemplate,
        selections,
        setSelections,
        setSelectedTransformedImage,
        selectedTransformedImage,
        repositioningRow,
        setRepositioningRow,
        imageTransforms,
        setImageTransforms,
        selectedImageData,
        setSelectedImageData,
        sceneTransforms,
        setSceneTransforms,
        selectedSceneId,
        setSelectedSceneId,
        userLayersByDesign,
        dispatchUserLayers,
        userLayersEnabled,
        visibility,
        setVisibility,
        resetTemplateContext
    } = useTemplate();
    const {isMobile} = useMobile()
    const {applyContentEditable} = useContentEditable(template)
    const templateContainerRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()
    const {t} = useTranslation()

    // Phone-first "Fill Mode" (checklist + bottom sheets) IS the mobile view;
    // desktop keeps the classic two-panel editor. No user-facing switch.
    const fillMode = isMobile;
    const [imageGridOpen, setImageGridOpen] = React.useState(false);

    // ------------------------------------------------------------------
    // USER LAYERS (docs/user-layers-plan.md, Part B)
    // ------------------------------------------------------------------
    const activeDesign = template?.designs?.[selectedDesign];
    /** The scene EVERY surface below renders/edits: template elements + this
     *  design's user layers. `activeDesign.scene` stays the raw one — layer
     *  `order` is an index into IT, so slot math must never use the merged one. */
    const mergedScene = useMergedScene(activeDesign, selectedDesign);
    const activeDesignKey = designKeyOf(activeDesign, selectedDesign);
    const activeLayers = userLayersByDesign[activeDesignKey] ?? [];
    const layerSlots = useMemo(() => validSlots(activeDesign?.scene), [activeDesign?.scene]);
    /** Where a layer's gallery/upload lands. There is no end-user "list my
     *  folders" endpoint (only GET /folder/{id}), so the template's own first
     *  image folder is the only address available; with none, image layers are
     *  not offered at all. */
    const layerFolderId = useMemo(() => {
        const inputs = template?.inputs ?? {};
        for (const key of Object.keys(inputs)) {
            const input = inputs[key];
            if (input.type === 'image' && (input as ImageInput).folderId) return (input as ImageInput).folderId!;
        }
        return undefined;
    }, [template]);
    /** An image layer is only CREATED once an asset is chosen — a source-less
     *  image element hard-fails the export renderer. This holds the id the
     *  gallery/upload is filling until that happens; abandoning the picker
     *  simply leaves it unused. */
    const pendingImageLayerRef = useRef<{ id: string; designKey: string; order: number } | null>(null);
    /** Delete is undoable through a snackbar (the end user has no undo stack). */
    const [layerUndo, setLayerUndo] = useState<{
        designKey: string;
        layer: UserLayer;
        index: number;
        selection?: string | ImageValue;
        transform?: ElementTransform;
    } | null>(null);
    const [textAccordionOpen, setTextAccordionOpen] = useState(true);
    const [imageAccordionOpen, setImageAccordionOpen] = useState(true);
    const [showUnusedOnly, setShowUnusedOnly] = useState(false);

    // Per-user draft ("continue where I left off"). hydratedRef gates the
    // auto-save so the initial default-fill never PUTs over an existing draft.
    // (After a successful restore one debounced PUT of the just-restored
    // payload can still fire — idempotent, tolerated.) saveTimerRef holds the
    // pending debounced save. draftGenRef invalidates an in-flight draft GET
    // when the user switches templates before it resolves — without it, the
    // old template's draft would merge into (and then auto-save onto) the new
    // template wherever input keys overlap.
    const hydratedRef = useRef(false);
    const saveTimerRef = useRef<number | null>(null);
    const draftGenRef = useRef(0);

    /** Fetch and apply this user's saved draft. Best-effort: a 404 (no draft) or
     *  any error just leaves the freshly-initialized defaults in place. */
    const restoreDraft = (loadedTemplate: Template, draftTemplateId: string, generation: number) => {
        apiService.api.get<{ payload: string; updatedAt?: string }>(`/template/${draftTemplateId}/draft`)
            .then((res) => {
                if (generation !== draftGenRef.current) return; // switched away mid-flight
                const parsed = JSON.parse(res.data.payload ?? '{}') as {
                    selections?: { [key: string]: string | ImageValue };
                    sceneTransforms?: SceneTransforms;
                    selectedDesign?: number;
                    contentRevision?: number;
                    userLayersByDesign?: unknown;
                    visibility?: unknown;
                };
                // If the template was re-synced from Figma since this draft was
                // saved, its geometry (sceneTransforms) is pruned by the id-match
                // below. Let the user know their repositions were dropped — a
                // one-time, non-blocking notice (restoreDraft runs once per load).
                const behind = isDraftBehindTemplate(parsed.contentRevision, loadedTemplate.contentRevision);
                if (behind) {
                    toast.info(t('editor.draftUpdatedNotice'));
                }
                // User layers FIRST: their ids are the valid-key set the two
                // filters below need (a layer is neither a template input nor a
                // scene element id, so both would otherwise drop it). Validation
                // enforces the flag, the cap, known design keys, well-formed ids
                // and — for image layers — an actually picked asset. A legacy
                // draft has no such field and hydrates to {}.
                //
                // A draft that is BEHIND loses its layers with its geometry:
                // layer `order` is an ABSOLUTE index into scene.elements, and a
                // re-sync is exactly what invalidates that premise. Restoring
                // them would silently restack the user's work against a board
                // they never saw.
                const restoredLayers: UserLayersByDesign = behind
                    ? {}
                    : parseUserLayers(parsed.userLayersByDesign, loadedTemplate, parsed.selections as any);
                const layerIndex = buildLayerIndex(restoredLayers, loadedTemplate);
                const layerIds = collectUserLayerIds(restoredLayers);
                // selections: restore only keys the template still has, so a stale
                // draft can't resurrect a field that was removed since it was saved.
                if (parsed.selections) {
                    setSelections(prev => {
                        const merged = {...prev};
                        for (const key of Object.keys(parsed.selections!)) {
                            if (loadedTemplate.inputs?.[key] || layerIds.has(key)) merged[key] = parsed.selections![key];
                        }
                        return merged;
                    });
                }
                // sceneTransforms: restore only element ids that still exist in some
                // design's scene (element ids change on re-import).
                if (parsed.sceneTransforms) {
                    const validIds = collectSceneElementIds(loadedTemplate);
                    const restored: SceneTransforms = {};
                    for (const id of Object.keys(parsed.sceneTransforms)) {
                        if (validIds.has(id) || layerIds.has(id)) restored[id] = parsed.sceneTransforms![id];
                    }
                    // Layer geometry is client-written JSON: a non-finite number
                    // in it silently blanks the node, so drop such an entry.
                    const clean = sanitizeLayerTransforms(restored, layerIndex);
                    if (Object.keys(clean).length) setSceneTransforms(clean);
                }
                if (Object.keys(restoredLayers).length) {
                    dispatchUserLayers({type: 'hydrate', value: restoredLayers});
                }
                // Hidden rows and flipped switches: only the ones this template
                // still has. Kept even when the draft is BEHIND — they name rows
                // and switches, never element ids or slots, so a re-sync cannot
                // make them point at the wrong thing.
                setVisibility(restoreVisibility(parsed.visibility, designScenes(loadedTemplate), loadedTemplate.toggles));
                // selectedDesign clamped to a valid index.
                if (typeof parsed.selectedDesign === 'number' && loadedTemplate.designs.length) {
                    const clamped = Math.min(Math.max(0, Math.floor(parsed.selectedDesign)), loadedTemplate.designs.length - 1);
                    setSelectedDesign(clamped);
                }
                // Push restored values onto the (legacy HTML) board; scene renders
                // reactively from `selections`.
                setShouldUpdateDesignValues(true);
            })
            .catch(() => {
                // No draft / parse error / network: keep defaults.
            })
            .finally(() => {
                if (generation === draftGenRef.current) hydratedRef.current = true;
            });
    };

    // Fixed issue: Added an empty dependency array to prevent useEffect from running on every render
    useEffect(() => {
        // A new template load: block auto-save, drop any pending one, and
        // invalidate any in-flight draft GET from the previous template.
        hydratedRef.current = false;
        draftGenRef.current += 1;
        // A pending image-layer pick and an undo offer both name ids that only
        // exist for the template we are leaving.
        pendingImageLayerRef.current = null;
        setLayerUndo(null);
        if (saveTimerRef.current) {
            window.clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        apiService.api.get<Template>(`/template/${templateId}`)
            .then((res: AxiosResponse<Template>) => {
                const template = res.data
                setSelectedDesign(0);
                resetTemplateContext()
                setTemplate(template);
                initSelection(template);
                if (templateId) restoreDraft(template, templateId, draftGenRef.current);
            })
            .catch((res: AxiosError<{ code: string, subcode: number }>) => {
                if (res.response?.data?.code === "0000/1" && res.response?.data?.subcode === 0) {
                    toast.error('אין לך הרשאה מתאימה לערוך את הטמפלט');
                } else {
                    toast.error('תקלה בעת משיכת הטמפלט');
                }
                logger.debug(res)
                navigate('/')
            })
    }, [templateId]);

    // Set by handleDesignReset so the state changes IT causes don't re-persist a
    // "defaults" draft right after the DELETE — a stale defaults-draft would
    // mask later admin changes to the template's default values.
    const skipNextDraftSaveRef = useRef(false);

    // Auto-save the working state (selections + scene transforms + selected
    // design), debounced ~1.5s. Runs at the TemplateEditor level so both Fill
    // Mode and the classic editor persist. Gated on hydratedRef so it never
    // fires for the initial defaults or the restore.
    useEffect(() => {
        if (!hydratedRef.current || !templateId) return;
        if (skipNextDraftSaveRef.current) {
            skipNextDraftSaveRef.current = false;
            return;
        }
        if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = window.setTimeout(() => {
            saveTimerRef.current = null;
            const payload = JSON.stringify({selections, sceneTransforms, selectedDesign, userLayersByDesign, visibility, contentRevision: template?.contentRevision});
            apiService.api.put(`/template/${templateId}/draft`, {payload}).catch(() => {});
        }, 1500);
        return () => {
            if (saveTimerRef.current) {
                window.clearTimeout(saveTimerRef.current);
                saveTimerRef.current = null;
            }
        };
    }, [selections, sceneTransforms, selectedDesign, userLayersByDesign, visibility, templateId]);

    // Flush a PENDING save when the tab is hidden/closed. fetch keepalive so the
    // request survives page teardown (axios/XHR would be cancelled). sendBeacon
    // can't carry the JWT Authorization header, so it's unusable here.
    useEffect(() => {
        const flush = () => {
            if (!hydratedRef.current || !templateId || !saveTimerRef.current) return;
            window.clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
            const payload = JSON.stringify({selections, sceneTransforms, selectedDesign, userLayersByDesign, visibility, contentRevision: template?.contentRevision});
            const token = getAccessToken();
            const base = config.apiUrl.replace(/\/+$/, '');
            fetch(`${base}/template/${templateId}/draft`, {
                method: 'PUT',
                keepalive: true,
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? {Authorization: `Bearer ${token}`} : {}),
                },
                body: JSON.stringify({payload}),
            }).catch(() => {});
        };
        window.addEventListener('pagehide', flush);
        return () => window.removeEventListener('pagehide', flush);
    }, [selections, sceneTransforms, selectedDesign, userLayersByDesign, visibility, templateId]);

    useEffect(() => {
        // console.log("selections123123123", selections)
    }, [selections]);

    const initSelection = (template: Template) => {

        const inputs = template.inputs
        const selectionsDefaults: {
            [key: string]: string | ImageValue
        } = {}
        Object.keys(inputs)
            .map(key => {
                const input = inputs[key]
                if (input.type == 'image') {
                    selectionsDefaults[key] = {
                        name: input.default.name ?? "תמונה",
                        highRes: input.default.highRes,
                        thumbnail: input.default.thumbnail,
                        width: input.default.width,
                        height: input.default.height,
                        type: input.default.type,
                        // Preserve the default's asset identity when the config
                        // carries one — deriveImageFit compares ids first.
                        assetId: input.default.assetId
                    }
                } else if (input.type == 'text') {
                    selectionsDefaults[key] = input.default
                }
            })
        setSelections(selectionsDefaults)
    };

    /**
     * Handles fields imported from Excel.
     * Merges the imported fields with the current selections and triggers a design update.
     */
    const handleExcelFieldsImported = (importedFields: Record<string, string | ImageValue>) => {
        setSelections(prevSelections => ({
            ...prevSelections,
            ...importedFields
        }));
        // Trigger design value update to reflect changes in the template display
        setShouldUpdateDesignValues(true);
    };

    function applyImagesCallback(inputs: { [p: string]: ImageInput | TextInput }) {

        const editableInputs = Object.keys(inputs)
            .filter(key => {
                const input = inputs[key];
                return input.editable && input.type == "image"
            });

        [...templateContainerRef.current!.querySelectorAll<HTMLElement>('[data-name]')]
            .filter(node => editableInputs.includes(node.getAttribute('data-name')!))
            .forEach(node => {
                const inputKey = node.getAttribute('data-name')!
                node.setAttribute('imageeditable', 'true');
                node.onclick = () => {
                    const imageInput = inputs[inputKey] as ImageInput
                    handleImageClick({
                        elementKey: inputKey,
                        label: imageInput.placeholder,
                        value: imageInput.default,
                        folderId: imageInput.folderId!
                    })
                }
                node.style.cursor = "pointer"
            })

        const nonEditableInputs = Object.keys(inputs)
            .filter(key => {
                const input = inputs[key];
                return !input.editable && input.type == "image"
            });

        const nonEditableContainers: string[] = [];
        [...templateContainerRef.current!.querySelectorAll<HTMLElement>('[data-name]')]
            .filter(node => nonEditableInputs.includes(node.getAttribute('data-name')!))
            .forEach(node => {
                node.style.pointerEvents = "none"

                // apply no pointer events for non-editable containers as well
                nonEditableContainers.push(`${node.id}-container`)
            });

        // console.log("nonEditableContainers", nonEditableContainers);
        [...templateContainerRef.current!.querySelectorAll<HTMLElement>('.image-container')]
            .filter(node => nonEditableContainers.includes(node.id))
            .forEach(node => {
                node.style.pointerEvents = "none"
            })
    }

    useEffect(() => {
        if (template != null && templateContainerRef != null) {
            applyContentEditable(templateContainerRef.current!, handleChangeTextFromContentEditable)
            applyImagesCallback(template.inputs!)
            // setImageTransforms({})
        }
    }, [template, selectedDesign, templateContainerRef])

    useEffect(() => {
        if (!template || !templateContainerRef?.current) return;

        const elementId = templateContainerRef.current!.id
        for (let [name, font] of Object.entries(template.fonts)) {
            if (font) {
                loadFont(name, font.urls!, elementId);
            }
        }
        // console.log("fonts added")
    }, [template, selectedDesign, templateContainerRef])

    const containerHeight = 500;

    function fitImageToContainer(imageInputWidth: number | undefined, imageInputHeight: number | undefined, assetWidth: number | undefined, assetHeight: number | undefined, element: HTMLImageElement, key: string) {
        if (imageInputWidth && imageInputHeight && assetWidth && assetHeight) {
            const {
                width,
                height,
                left,
                top
            } = handleImageChangeByWidthAndHeight(imageInputWidth, imageInputHeight, assetWidth, assetHeight)

            // Apply inline styles
            element.style.width = `${width}px`;
            element.style.height = `${height}px`;
            element.style.left = `${left}px`;
            element.style.top = `${top}px`;

            setImageTransforms((prev) => ({
                ...prev,
                [key]: {
                    left,
                    top,
                    width,
                    height,
                    transformed: prev?.[key]?.transformed ?? false,
                },
            }))
        }
    }

    const handleImageChange = (asset: Asset, key: string) => {
        // A USER LAYER has no template input and no legacy [data-name] DOM node:
        // the scene renders it straight from `selections`, so all the sizing /
        // video-thumbnail juggling below is skipped. This branch also COMMITS a
        // pending image layer — the layer is created only now that an asset
        // exists (a source-less image element hard-fails the export renderer).
        if (asset && isUserLayerId(key)) {
            // A layer is image-only: there is no field config to opt a layer
            // into video, and the MP4 composite path hole-punches exactly ONE
            // video element chosen from the template's inputs.
            if (isVideoAssetValue(asset)) {
                pendingImageLayerRef.current = null;
                toast.error(t('editor.layers.videoNotSupported'));
                return;
            }
            setSelections(prevSelections => ({
                ...prevSelections,
                [key]: {
                    name: asset.name,
                    highRes: asset.highRes,
                    thumbnail: asset.thumbnail,
                    height: asset.height,
                    width: asset.width,
                    type: asset.type,
                    duration: asset.duration,
                    assetId: asset.id,
                },
            }));
            const pending = pendingImageLayerRef.current;
            if (pending && pending.id === key) {
                pendingImageLayerRef.current = null;
                // COMMIT the starting box now. Deriving it from the asset ratio
                // on every merge would move the box each time the user replaces
                // the picture — "replace this image" must not also relocate it.
                const scene = template?.designs?.[selectedDesign]?.scene;
                if (scene) {
                    const box = initialLayerBox(scene, 'image', {
                        highRes: asset.highRes, width: asset.width, height: asset.height,
                    });
                    setSceneTransforms(prev => ({...prev, [key]: {...prev[key], ...box}}));
                }
                dispatchUserLayers({
                    type: 'add',
                    designKey: pending.designKey,
                    layer: {id: key, kind: 'image', order: pending.order},
                });
            }
            return;
        }
        // console.log("event.target.value", event.target.value)
        if (asset) { // Check if the conversion was successful
            const imageInput = template!.inputs![key] as ImageInput; // Use the index to access the selected value
            const selectedValue = asset; // Use the index to access the selected value
            const selectedType = template!.inputs![key].type; // Access other necessary data
            // console.log("handleImageChange selectedValueselectedValue", key, selectedValue)


            // Now you can use `selectedValue` and `selectedType` as needed
            // Example: Updating selections with the selected value
            setSelections(prevSelections => ({
                ...prevSelections,
                [key]: {
                    name: selectedValue.name,
                    highRes: selectedValue.highRes,
                    thumbnail: selectedValue.thumbnail,
                    height: selectedValue.height,
                    width: selectedValue.width,
                    type: selectedValue.type,
                    duration: selectedValue.duration,  // Preserve video duration
                    // The asset identity, so a "did the user replace this?"
                    // check can compare ids instead of guessing from URLs.
                    assetId: selectedValue.id
                }
            })); // Update selections state

            const designKey = template!.designs[selectedDesign].key ?? ''
            const imgElements = document.querySelectorAll(`[data-name="${key}"]`) as NodeListOf<HTMLImageElement>;
            // console.log("imgElement", imgElements, key)
            if (imgElements) {
                imgElements.forEach(async element => {
                    // Replace the image's src attribute with the new value
                    element.src = selectedValue.thumbnail;

                    const imageInputWidth = imageInput.size?.[designKey]?.width;
                    const imageInputHeight = imageInput.size?.[designKey]?.height;
                    fitImageToContainer(imageInputWidth, imageInputHeight, asset.width, asset.height, element, key);

                    // Apply video thumbnail styling if it's a video asset
                    if (isVideoAsset(asset)) {
                        const compositeDimensions = await applyVideoThumbnailStyle(element, selectedValue.thumbnail);
                        // Update imageTransforms with container dimensions (not cover dimensions)
                        if (compositeDimensions) {
                            setImageTransforms((prev) => ({
                                ...prev,
                                [key]: {
                                    left: 0,
                                    top: 0,
                                    width: compositeDimensions.width,
                                    height: compositeDimensions.height,
                                    transformed: prev?.[key]?.transformed ?? false,
                                },
                            }));
                        }
                    }
                })
            }
        }
    };

    const handleChangeText = (
        event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
        key: string,
        updateInputs = true
    ) => {
        const value = event.target.value ?? event.target.innerText
        // Directly use event.target.value for text fields
        setSelections(prevSelections => ({...prevSelections, [key]: value}));

        // console.log("text changed", key, event.target.value ?? event.target.innerText)
        // console.log("text changed 1", key, event)
        // console.log("Selections", selections)

        if (updateInputs) {
            const textElements = document.querySelectorAll(`[data-name="${key}"]`);
            if (textElements) {
                // Replace the text content with the new value
                textElements.forEach(element => {
                    element.textContent = value
                })
            }
        }
    };

    const handleChangeTextFromContentEditable =
        (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, key: string) =>
            handleChangeText(event, key, false)

    /** Everything that points at ONE design's element or at an in-flight picker.
     *  Carried across a design switch (or a template load, or a reset) these
     *  would leave the Transformer attached to an element that is no longer on
     *  screen, or land a picked asset on the previous design's layer. */
    const clearTransientLayerState = useCallback(() => {
        setSelectedSceneId(null);
        setSelectedTransformedImage(undefined);
        setRepositioningRow(null);
        setSelectedImageData(undefined);
        setImageGridOpen(false);
        pendingImageLayerRef.current = null;
        setLayerUndo(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDesignChange = (index: number) => {
        if (index === selectedDesign) return;
        // Layers are PER DESIGN: a selection/reposition/pending pick belonging to
        // the design we are leaving must not survive the switch.
        clearTransientLayerState();
        setSelectedDesign(index)
    }

    const onTemplateLoad = () => {
        setShouldUpdateDesignValues(true);
    }

    const handleDesignReset = () => {
        // Reset is reset: revert ALL user edits to the imported design.
        // Also drop the saved draft so a later reload starts clean; the state
        // changes below would re-persist a "defaults" draft via the auto-save,
        // so skip that one save (see skipNextDraftSaveRef).
        // Kill the pending debounced save BEFORE the DELETE: otherwise a save
        // scheduled a moment ago lands after it and re-creates the draft we
        // just deleted. skipNextDraftSaveRef only covers the save the state
        // changes below will schedule.
        if (saveTimerRef.current) {
            window.clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        if (templateId) apiService.api.delete(`/template/${templateId}/draft`).catch(() => {});
        skipNextDraftSaveRef.current = true;
        initSelection(template!);          // text/image values -> defaults
        setImageTransforms({});            // image crop/position within its frame
        setSceneTransforms({});            // scene canvas inline move/resize/rotate
        setSelectedSceneId(null);          // clear canvas selection
        dispatchUserLayers({type: 'reset'}); // the user's own layers are user edits too
        setVisibility(DEFAULT_VISIBILITY);  // every row shown, every switch at its default
        clearTransientLayerState();
        setShouldUpdateDesignValues(true)
    }

    // calls on template reset/change design
    useEffect(() => {
        if (shouldUpdateDesignValues) {
            setShouldUpdateDesignValues(false)
            // remove transform selection on design change
            setSelectedTransformedImage(undefined)
            // A scene design has no [data-name] DOM at all — it renders from
            // `selections` reactively — so this whole legacy sync is a no-op
            // there. Skipping it also keeps user-layer keys (which are not
            // template inputs) away from the input lookups below.
            if (template?.designs?.[selectedDesign]?.scene) return;
            for (const key of Object.keys(selections)) {

                const selection = selections[key]
                // A key with no template input is a USER LAYER (or a stale
                // selection): there is nothing in the legacy DOM to sync, and
                // `.type` on undefined would throw and take the editor down.
                const input = template?.inputs?.[key];
                if (!input) continue;

                const textElements = document.querySelectorAll(`[data-name="${key}"]`);
                if (input.type == "text" && textElements) {
                    // console.log("textElements", textElements)
                    // Replace the text content with the new value
                    textElements.forEach(element => {
                        element.textContent = selection as string; // Assuming 'name' holds the text you want to display
                    })
                }

                const imgElements = document.querySelectorAll(`[data-name="${key}"]`) as NodeListOf<HTMLImageElement>;
                if (input.type == "image" && imgElements) {
                    // console.log("imgElements", imgElements)
                    const imageInput = template!.inputs![key] as ImageInput;
                    const designKey = template!.designs[selectedDesign].key ?? ''
                    imgElements.forEach(async element => {
                        // Replace the image's src attribute with the new value
                        const imageValue = selection as ImageValue
                        element.src = imageValue.thumbnail;

                        const imageInputWidth = imageInput.size?.[designKey]?.width;
                        const imageInputHeight = imageInput.size?.[designKey]?.height;

                        const selectedDesignKey = template!.designs[selectedDesign].key
                        // if there is no design key, fall to the image value to support old versions
                        const isSameUrl = !!selectedDesignKey && imageInput.default.thumbnail == imageValue.thumbnail
                        const isTransformed = imageTransforms?.[key]?.transformed ?? false

                        // Check if this is a video asset before applying fitImageToContainer
                        const tempAsset = {
                            highRes: imageValue.highRes,
                            thumbnail: imageValue.thumbnail,
                            type: imageValue.type || '' // Use actual type or fallback to URL extension check
                        } as Asset;
                        const isVideo = isVideoAsset(tempAsset);

                        if (imageTransforms?.[key] && isTransformed) {
                            logger.debug("imageTransforms?.[key]", key, imageTransforms?.[key])
                            const {width, height, top, left} = imageTransforms?.[key]
                            element.style.position = 'relative';
                            element.style.width = `${width}px`;
                            element.style.height = `${height}px`;
                            element.style.left = `${left}px`;
                            element.style.top = `${top}px`;
                        } else {
                            fitImageToContainer(imageInputWidth, imageInputHeight,
                                isSameUrl && !isTransformed && imageInput.size?.[selectedDesignKey]?.width ? imageInput.size[selectedDesignKey].width! : imageValue?.width,
                                isSameUrl && !isTransformed && imageInput.size?.[selectedDesignKey]?.height ? imageInput.size[selectedDesignKey].height! : imageValue?.height,
                                element, key);
                        }

                        // Apply video thumbnail styling if it's a video asset
                        if (isVideo) {
                            const compositeDimensions = await applyVideoThumbnailStyle(element, imageValue.thumbnail);
                            // Update imageTransforms with container dimensions (not cover dimensions)
                            if (compositeDimensions) {
                                setImageTransforms((prev) => ({
                                    ...prev,
                                    [key]: {
                                        left: 0,
                                        top: 0,
                                        width: compositeDimensions.width,
                                        height: compositeDimensions.height,
                                        transformed: prev?.[key]?.transformed ?? false,
                                    },
                                }));
                            }
                        }
                    })
                }
            }
        }
    }, [selectedDesign, shouldUpdateDesignValues])

    // A hidden row or a switched-off layer takes its elements off the board. A
    // selection or reposition left pointing at one keeps the renderer's
    // reposition lock with nothing to move — every other element goes inert.
    useEffect(() => {
        if (!activeDesign?.scene || !mergedScene) return;
        if (selectedSceneId && !hasSceneElementId(mergedScene.elements, selectedSceneId)) setSelectedSceneId(null);
        if (selectedTransformedImage && !findSceneElementByInputKey(mergedScene.elements, selectedTransformedImage)) {
            setSelectedTransformedImage(undefined);
        }
        // Same for a row being moved: hidden (or gone after a re-sync), it
        // would leave the canvas in row mode with nothing live on it.
        if (repositioningRow && !mergedScene.elements.some((el) => el.groupId === repositioningRow)) {
            setRepositioningRow(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDesign?.scene, mergedScene, selectedSceneId, selectedTransformedImage, repositioningRow]);

    /** Row reposition ("מיקום מחדש" on a row): move the whole row as one unit.
     *  Entering drops every other canvas interaction — a single-element
     *  reposition and a row move are mutually exclusive locks. Passing null, or
     *  the row already being moved, exits (the "אישור" button). */
    const handleRowReposition = (rowKey: string | null) => {
        if (!rowKey || rowKey === repositioningRow) {
            setRepositioningRow(null);
            return;
        }
        setSelectedTransformedImage(undefined);
        setSelectedSceneId(null);
        setSelectedImageData(undefined);
        setRepositioningRow(rowKey);
    };

    /** "אפס מיקום": put the row back where the design has it. Reads the FULL
     *  template scene so a member switched off right now is reset too. */
    const handleRowRepositionReset = () => {
        const scene = activeDesign?.scene;
        if (!repositioningRow || !scene) return;
        const rowKey = repositioningRow;
        setSceneTransforms((prev) => resetRow(prev, scene, rowKey));
    };

    const handleImageTransform = (inputKey: string | undefined) => {
        // Nothing on the board to reposition (a hidden row's field, reached
        // from its desktop accordion): don't lock the canvas onto it.
        if (inputKey && template?.designs?.[selectedDesign]?.scene
            && !findSceneElementByInputKey(mergedScene?.elements, inputKey)) return;
        // Entering a single-element reposition ends a row move.
        if (inputKey) setRepositioningRow(null);
        // 4) Set as the currently selected image
        setSelectedTransformedImage(inputKey);
        // Reposition is a CANVAS gesture: the panel must stay visible next to it.
        // `imageGridOpen` is sticky (it survives a gallery close), so without
        // this an earlier gallery visit swapped the layer panel for the grid the
        // moment the user hit Reposition.
        if (inputKey) setImageGridOpen(false);

        // Scene (canvas) engine: select the bound element on the canvas so the
        // Konva Transformer appears (drag/resize). There are no [data-name] DOM
        // nodes on the canvas, so skip the legacy DOM pointer-events juggling.
        const sceneDesign = template?.designs?.[selectedDesign];
        if (sceneDesign?.scene) {
            // MERGED scene: a user layer only exists there.
            const el = findSceneElementByInputKey((mergedScene ?? sceneDesign.scene).elements, inputKey);
            setSelectedSceneId(el?.id ?? null);
            if (inputKey && isUserLayerId(inputKey)) {
                // Synthesize the image-selection record so the desktop floating
                // control bar (reset / approve) appears for an image layer too.
                // A TEXT layer has no such bar — it is repositioned from its card.
                const layerValue = selections[inputKey];
                if (layerValue && typeof layerValue === 'object') {
                    setSelectedImageData({
                        elementKey: inputKey,
                        label: t('editor.layers.imageLayer'),
                        value: layerValue as ImageValue,
                        folderId: layerFolderId ?? '',
                    });
                }
                return;
            }
            // Open the bottom floating control bar (zoom / Reset / Approve) even
            // when reposition is triggered from the form panel — a canvas tap sets
            // selectedImageData, but the form-panel Reposition link did not, so the
            // bar (and the zoom controls) never appeared there.
            const input = inputKey ? template?.inputs?.[inputKey] : undefined;
            if (input && input.type === 'image' && (input as ImageInput).editable) {
                const imageInput = input as ImageInput;
                const current = selections[inputKey!] as ImageValue | undefined;
                setSelectedImageData({
                    elementKey: inputKey!,
                    label: imageInput.placeholder,
                    value: current ?? imageInput.default,
                    folderId: imageInput.folderId!,
                });
            }
            return;
        }

        const editableInputs = Object.keys(template?.inputs ?? {})
            .filter(key => {
                const input = template!.inputs[key];
                return input.editable && input.type == "image" && inputKey != key
            });

        [...templateContainerRef.current!.querySelectorAll<HTMLElement>('[data-name]')]
            .filter(node => editableInputs.includes(node.getAttribute('data-name')!))
            .forEach(node => {
                node.parentElement?.classList.add("no-pointer-events")
            })
    }

    useEffect(() => {
        if (selectedTransformedImage == undefined) {
            [...templateContainerRef?.current?.querySelectorAll<HTMLElement>('.no-pointer-events') ?? []]
                .forEach(node => {
                    node.classList.remove("no-pointer-events")
                })
        }
    }, [selectedTransformedImage])

    const handleOpenShareModal = () => {
        setShareModalOpen(true);
    };

    const handleCloseShareModal = () => {
        setShareModalOpen(false);
    };

    // Fill Mode is checked through a ref because legacy [data-name] onclick
    // handlers are wired in an effect that doesn't re-run on mode switches.
    const fillModeRef = useRef(fillMode);
    fillModeRef.current = fillMode;

    function handleImageClick(imageData: SelectedImageData) {
        setSelectedImageData(imageData);
        setSelectedTransformedImage(undefined);
        // The gallery + image bar take over: a row move left on would keep the
        // canvas locked to the row underneath them. (Every gallery entry point
        // — panel, canvas tap, image-layer add/replace — comes through here.)
        setRepositioningRow(null);
        // Fill Mode replaces the classic in-panel gallery with its own sheets;
        // a legacy canvas tap there only surfaces the floating reposition bar.
        if (!fillModeRef.current) setImageGridOpen(true)
    }

    /** Fill Mode: nudge a text element's font size ±10% per tap (text sheet's
     *  − / + steppers). Same absolute-px sceneTransforms[el.id].fontSize override
     *  the desktop form panel writes (InputControls.handleFontStep), so preview
     *  and export honor it identically — but resolved with the RECURSIVE lookup
     *  so text nested in groups/collages is found too. */
    const handleTextFontStep = useCallback((inputKey: string, direction: 1 | -1) => {
        const sceneDesign = template?.designs?.[selectedDesign];
        if (!sceneDesign?.scene) return;
        // MERGED: a user text layer is only an element there.
        const el = findSceneElementByInputKey((mergedScene ?? sceneDesign.scene).elements, inputKey);
        if (el?.type !== 'text' || !el?.text) return;
        const base = el.text.fontSize || 16;
        setSceneTransforms(prev => {
            const current = prev[el.id]?.fontSize ?? base;
            const next = Math.min(2000, Math.max(4, Math.round(current * (direction > 0 ? 1.1 : 1 / 1.1) * 100) / 100));
            return {...prev, [el.id]: {...prev[el.id], fontSize: next}};
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, selectedDesign, mergedScene]);

    /** Fill Mode: end any image interaction (floating-bar close/approve) without
     *  the classic panel's grid-reopen behaviour. */
    const handleEndImageInteraction = () => {
        setSelectedImageData(undefined);
        setSelectedTransformedImage(undefined);
        setSelectedSceneId(null);
    };

    // Single-click on an image element on the scene canvas → open the asset
    // gallery for THAT input (mirrors the legacy HTML editor's per-element
    // onclick wiring in applyImagesCallback). Looks the input up by inputKey,
    // builds the same SelectedImageData shape handleImageClick expects.
    const handleSceneImageClick = useCallback((inputKey: string) => {
        if (isUserLayerId(inputKey)) {
            const value = selections[inputKey];
            if (!value || typeof value !== 'object' || !layerFolderId) return;
            handleImageClick({
                elementKey: inputKey,
                label: t('editor.layers.imageLayer'),
                value: value as ImageValue,
                folderId: layerFolderId,
            });
            return;
        }
        const input = template?.inputs?.[inputKey];
        if (!input || input.type !== 'image' || !input.editable) return;
        const imageInput = input as ImageInput;
        const current = selections[inputKey] as ImageValue | undefined;
        handleImageClick({
            elementKey: inputKey,
            label: imageInput.placeholder,
            value: current ?? imageInput.default,
            folderId: imageInput.folderId!,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, selections, layerFolderId]);

    function handleOnCloseImageSelection() {
        setSelectedImageData(undefined)
        setSelectedTransformedImage(undefined);
        setSelectedSceneId(null); // scene: clear the canvas selection on apply/close
        setImageGridOpen(true)
    }

    const resetImageTransform = (imageKey?: string, imageValue?: ImageValue) => {
        // `selectedImageData` is IMAGE-only, but reset is reachable for a TEXT
        // target too (Fill Mode long-press -> StickyBar reset, and a text layer
        // card). Fall back to the repositioning key, then bail — the old
        // non-null assertion crashed on exactly those paths.
        const elementKey = imageKey ?? selectedTransformedImage ?? selectedImageData?.elementKey;
        if (!elementKey) return;

        // Scene engine: "reset position" = drop this element's inline transform.
        const sceneDesign = template?.designs?.[selectedDesign];
        if (sceneDesign?.scene) {
            const el = findSceneElementByInputKey((mergedScene ?? sceneDesign.scene).elements, elementKey);
            if (el) setSceneTransforms(prev => {
                const next = {...prev};
                delete next[el.id];
                return next;
            });
            return;
        }

        const value = imageValue ?? selectedImageData?.value
        const selectedDesignKey = template!.designs[selectedDesign].key
        const imageInput = template?.inputs?.[elementKey] as ImageInput | undefined;
        // Legacy HTML path only: without an image input (a text target, or a
        // layer) there is no frame to re-fit to.
        if (!value || imageInput?.type !== 'image') return;
        const isSameUrl = imageInput.default.thumbnail == value.thumbnail;
        const {
            width,
            height,
            left,
            top
        } = handleImageChangeByWidthAndHeight(
            imageInput.size[selectedDesignKey].width!,
            imageInput.size[selectedDesignKey].height!,
            isSameUrl ? imageInput.size[selectedDesignKey].width! : value.width!,
            isSameUrl ? imageInput.size[selectedDesignKey].height! : value.height!
        );

        setImageTransforms(prevState => ({
            ...prevState,
            [elementKey]: {
                width,
                height,
                left,
                top,
                transformed: false,
                reset: true
            }
        }));
    };

    // ------------------------------------------------------------------
    // USER LAYER ACTIONS
    // ------------------------------------------------------------------
    /** True while this design is already at the cap; the caller toasts and stops
     *  rather than silently dropping the tap. */
    const layerCapReached = activeLayers.length >= MAX_USER_LAYERS_PER_DESIGN;
    const warnLayerCap = () => {
        toast.info(t('editor.layers.limitReached', {max: MAX_USER_LAYERS_PER_DESIGN}));
    };

    /** New layers land on TOP of the whole stack (the last legal slot). */
    const topSlot = () => layerSlots[layerSlots.length - 1] ?? 0;

    const handleAddTextLayer = () => {
        if (!userLayersEnabled || !activeDesign?.scene) return;
        if (layerCapReached) return warnLayerCap();
        // No `style.color`: absent means "inherit the reference text element's
        // colour" (resolved at merge time), which is the spec's default. Writing
        // white here would override the design's own colour.
        const layer: UserLayer = {id: newUserLayerId(), kind: 'text', order: topSlot()};
        setSelections(prev => ({...prev, [layer.id]: t('editor.layers.defaultText')}));
        setSceneTransforms(prev => ({
            ...prev,
            [layer.id]: {...prev[layer.id], ...initialLayerBox(activeDesign!.scene!, 'text')},
        }));
        dispatchUserLayers({type: 'add', designKey: activeDesignKey, layer});
    };

    /** Reserve an id for an image layer and hand it to the picker. The layer
     *  itself is created in handleImageChange, once an asset actually exists. */
    const beginAddImageLayer = (): string | null => {
        if (!userLayersEnabled || !activeDesign?.scene || !layerFolderId) return null;
        if (layerCapReached) {
            warnLayerCap();
            return null;
        }
        const id = newUserLayerId();
        pendingImageLayerRef.current = {id, designKey: activeDesignKey, order: topSlot()};
        return id;
    };

    /** Desktop: open the in-panel gallery on a brand-new image layer. */
    const handleAddImageLayer = () => {
        const id = beginAddImageLayer();
        if (!id || !layerFolderId) return;
        handleImageClick({
            elementKey: id,
            label: t('editor.layers.imageLayer'),
            value: {name: '', highRes: '', thumbnail: ''},
            folderId: layerFolderId,
        });
    };

    /** Desktop: re-open the gallery on an EXISTING image layer. */
    const handleReplaceLayerImage = (id: string) => {
        const value = selections[id];
        if (!layerFolderId || !value || typeof value !== 'object') return;
        handleImageClick({
            elementKey: id,
            label: t('editor.layers.imageLayer'),
            value: value as ImageValue,
            folderId: layerFolderId,
        });
    };

    const handleLayerMove = (id: string, move: 'up' | 'down' | 'front' | 'back') => {
        dispatchUserLayers({type: 'move', designKey: activeDesignKey, id, move, slots: layerSlots});
    };

    const handleLayerStyle = (id: string, style: Partial<UserLayerStyle>) => {
        dispatchUserLayers({type: 'setStyle', designKey: activeDesignKey, id, style});
    };

    /** Delete is atomic across the three stores that hold a layer, and clears
     *  any selection pointing at it — otherwise the Transformer stays attached
     *  to an element that no longer exists. Undo restores all of it. */
    const handleLayerDelete = (id: string) => {
        const index = activeLayers.findIndex((l) => l.id === id);
        if (index < 0) return;
        setLayerUndo({
            designKey: activeDesignKey,
            layer: activeLayers[index],
            index,
            selection: selections[id],
            transform: sceneTransforms[id],
        });
        dispatchUserLayers({type: 'remove', designKey: activeDesignKey, id});
        setSelections(prev => {
            const next = {...prev};
            delete next[id];
            return next;
        });
        setSceneTransforms(prev => {
            const next = {...prev};
            delete next[id];
            return next;
        });
        if (selectedSceneId === id) setSelectedSceneId(null);
        if (selectedTransformedImage === id) setSelectedTransformedImage(undefined);
        if (selectedImageData?.elementKey === id) setSelectedImageData(undefined);
    };

    const handleLayerUndoDelete = () => {
        if (!layerUndo) return;
        const {designKey, layer, index, selection, transform} = layerUndo;
        dispatchUserLayers({type: 'restore', designKey, layer, index});
        if (selection !== undefined) setSelections(prev => ({...prev, [layer.id]: selection}));
        if (transform) setSceneTransforms(prev => ({...prev, [layer.id]: transform}));
        setLayerUndo(null);
    };

    const layerUndoSnackbar = (
        <LayerUndoSnackbar
            open={!!layerUndo}
            onUndo={handleLayerUndoDelete}
            onClose={() => setLayerUndo(null)}
        />
    );

    if (fillMode) {
        return (
            <>
                <Box p={0} m={0}>
                    {template?.inputs ? (
                        <FillModeEditor
                            template={template}
                            selections={selections}
                            selectedDesign={selectedDesign}
                            templateContainerRef={templateContainerRef}
                            onTextChange={handleChangeText}
                            onImageChange={handleImageChange}
                            onDesignChange={handleDesignChange}
                            onTemplateLoad={onTemplateLoad}
                            onReset={handleDesignReset}
                            onShare={handleOpenShareModal}
                            onReposition={handleImageTransform}
                            onRepositionReset={resetImageTransform}
                            onEndImageInteraction={handleEndImageInteraction}
                            onRowReposition={handleRowReposition}
                            onRowRepositionReset={handleRowRepositionReset}
                            onFontStep={handleTextFontStep}
                            userLayersEnabled={userLayersEnabled}
                            layers={activeLayers}
                            layerFolderId={layerFolderId}
                            onAddTextLayer={handleAddTextLayer}
                            onBeginAddImageLayer={beginAddImageLayer}
                            onLayerMove={handleLayerMove}
                            onLayerStyle={handleLayerStyle}
                            onLayerDelete={handleLayerDelete}
                            layerSlots={layerSlots}
                        />
                    ) : (
                        <ShimmerContainer>
                            <ShimmerEffect sx={{width: '100%', height: '600px'}}/>
                        </ShimmerContainer>
                    )}
                </Box>

                {/* Legacy DOM image reposition (Moveable over a [data-name] node). */}
                {!template?.designs?.[selectedDesign]?.scene && (
                    <ImageDragAndResize
                        templateContainerRef={templateContainerRef}/>
                )}

                {layerUndoSnackbar}

                <ShareModal
                    open={shareModalOpen}
                    onClose={handleCloseShareModal}
                    designIndex={selectedDesign}
                />
            </>
        );
    }

    return (
        <>
            {/* dont add position relative because of the info bar */}
            <Box p={0} m={0}>

                <TopBar/>

                <ContentEditableInfoBar/>
                <Container maxWidth='lg'>
                    <Grid
                        columns={{xs: 12, lg: 41}}
                        justifyContent="left"
                        container
                        direction={isMobile ? "column-reverse" : "row"}
                    >
                        <Grid item
                              xs={12} lg={20}>

                            {
                                template?.inputs ?
                                    <EditContainer
                                        sx={{
                                            padding: (theme: Theme) => `${theme.spacing(1)} ${theme.spacing(3)} ${theme.spacing(3)}`
                                        }}>

                                        {!imageGridOpen || !selectedImageData ?
                                            <>
                                                <InputControls
                                                    inputs={template.inputs}
                                                    selections={selections}
                                                    handleImageClick={handleImageClick}
                                                    handleChangeText={handleChangeText}
                                                    handleImageTransform={handleImageTransform}
                                                    handleResetImageTransform={resetImageTransform}
                                                    handleFontStep={handleTextFontStep}
                                                    selectedDesignKey={template!.designs[selectedDesign].key ?? ''}
                                                    selectedDesign={selectedDesign}
                                                    textAccordionOpen={textAccordionOpen}
                                                    setTextAccordionOpen={setTextAccordionOpen}
                                                    imageAccordionOpen={imageAccordionOpen}
                                                    setImageAccordionOpen={setImageAccordionOpen}
                                                    onRowReposition={handleRowReposition}
                                                />
                                                {userLayersEnabled && !!activeDesign?.scene && (
                                                    <UserLayersPanel
                                                        layers={activeLayers}
                                                        selections={selections}
                                                        scene={mergedScene}
                                                        selectedId={selectedSceneId}
                                                        slots={layerSlots}
                                                        repositioningKey={selectedTransformedImage}
                                                        canAddImage={!!layerFolderId}
                                                        onAddText={handleAddTextLayer}
                                                        onAddImage={handleAddImageLayer}
                                                        onTextChange={handleChangeText}
                                                        onFontStep={handleTextFontStep}
                                                        onStyle={handleLayerStyle}
                                                        onMove={handleLayerMove}
                                                        onDelete={handleLayerDelete}
                                                        onReplaceImage={handleReplaceLayerImage}
                                                        onReposition={handleImageTransform}
                                                    />
                                                )}
                                            </>
                                            :
                                            <ImageSelectionWrapper
                                                // Key by the selected input so switching target (e.g. tapping
                                                // a different canvas image while the gallery is open) remounts
                                                // with a fresh target instead of keeping the prior input's
                                                // folder/value seeded in its useState initializer.
                                                key={selectedImageData?.elementKey}
                                                onChange={handleImageChange}
                                                onClose={handleOnCloseImageSelection}
                                                showUnusedOnly={showUnusedOnly}
                                                setShowUnusedOnly={setShowUnusedOnly}
                                            />
                                        }
                                    </EditContainer>
                                    :
                                    <ShimmerContainer>
                                        <ShimmerEffect sx={{
                                            width: `100%`,
                                            height: `${450}px`
                                        }}>
                                        </ShimmerEffect>
                                    </ShimmerContainer>
                            }
                        </Grid>
                        <Grid item xs={12} lg={1} sx={{marginTop: theme => theme.spacing(2)}}>
                            {isMobile && (
                                <Box display="flex" justifyContent="center" gap={1}
                                     sx={{marginBottom: theme => theme.spacing(2)}}>
                                    {t('editor.title.editManually')}
                                    <KeyboardDoubleArrowDownIcon/>
                                </Box>
                            )}
                        </Grid>
                        <Grid item xs={12} lg={20}>
                            {template ?
                                <EditContainer padding={{
                                    xs: 2,
                                    lg: 3,
                                }} textAlign={{
                                    xs: 'center',
                                    lg: 'right',
                                }}>
                                    <Controls container>
                                        <AvailableDesignGrid
                                            designContainerHeight={containerHeight}
                                            selectedDesign={selectedDesign}
                                            handleDesignChange={handleDesignChange}
                                        />
                                        {!isMobile && <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Button variant={'text'}
                                                    sx={{
                                                        color: 'white'
                                                    }}
                                                    onClick={handleDesignReset}
                                            >
                                                {t('editor.buttons.resetTemplate')}
                                            </Button>
                                            <GenerateImageButton onClick={handleOpenShareModal}/>
                                        </Grid>}
                                    </Controls>
                                    <TemplateDisplay
                                        ref={templateContainerRef}
                                        design={template.designs[selectedDesign]}
                                        designIndex={selectedDesign}
                                        onTemplateLoad={onTemplateLoad}
                                        containerHeight={containerHeight}
                                        values={selections as any}
                                        fonts={template.fonts as any}
                                        onImageInputClick={handleSceneImageClick}
                                    />
                                    {/* Row reposition: reset / approve, right under the board. */}
                                    {repositioningRow && (
                                        <RowRepositionBar
                                            onReset={handleRowRepositionReset}
                                            onDone={() => handleRowReposition(null)}
                                        />
                                    )}
                                    {templateId && !selectedImageData && (
                                        <Grid item mt={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                                            <ExcelImportExport
                                                templateId={templateId}
                                                onFieldsImported={handleExcelFieldsImported}
                                            />
                                        </Grid>
                                    )}
                                    {isMobile && !selectedImageData && <Grid item mt={2}>
                                        <Button variant={'text'}
                                                sx={{
                                                    color: 'white'
                                                }}
                                                onClick={handleDesignReset}
                                        >
                                            {t('editor.buttons.resetTemplate')}
                                        </Button>
                                        <GenerateImageButton onClick={handleOpenShareModal}/>
                                    </Grid>}
                                    {selectedImageData && (
                                        <FloatingImageControlBarWrapper
                                            handleTransformClick={handleImageTransform}
                                            handleCloseClick={handleOnCloseImageSelection}
                                            handleResetClick={resetImageTransform}
                                            selectedDesign={selectedDesign}
                                        />
                                    )}
                                </EditContainer>
                                :
                                <ShimmerContainer>
                                    <ShimmerEffect sx={{
                                        width: `100%`,
                                        height: `${600}px`
                                    }}>
                                    </ShimmerEffect>
                                </ShimmerContainer>
                            }
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Legacy DOM image reposition (Moveable over a [data-name] node). Scene
                templates reposition on the Konva canvas instead. */}
            {!template?.designs?.[selectedDesign]?.scene && (
                <ImageDragAndResize
                    templateContainerRef={templateContainerRef}/>
            )}

            {layerUndoSnackbar}

            <ShareModal
                open={shareModalOpen}
                onClose={handleCloseShareModal}
                designIndex={selectedDesign}
            />
        </>
    );
};

export default TemplateEditor;

